import { useCallback, useMemo } from "react";
import { useTranslation as useReactI18NextTranslation } from "react-i18next";
import { setI18nLanguageStorage } from "../../shared/storageUtils";
import { setDayJSLocale } from "../dayjs/utils";
import { setMomentLocale } from "../moment/utils";
import { DEFAULT_LOCALE, Locales, SUPPORTED_LANGUAGES } from "./constants";
import { isSupportedLanguage } from "./utils";

type UseTranslationProps = Parameters<typeof useReactI18NextTranslation>;

export function useTranslations(...props: UseTranslationProps) {
  const { i18n, t } = useReactI18NextTranslation(...props);

  const supportedLanguages = useMemo(
    () =>
      Object.entries(SUPPORTED_LANGUAGES).map(([key, value]) => ({
        displayName: value.displayName,
        locale: key as Locales,
        name: value.name,
      })),
    []
  );

  const handleChangeLanguage = useCallback(
    async (languageLocale: Locales) => {
      if (!isSupportedLanguage(languageLocale)) {
        console.warn(
          `Language ${languageLocale} is not supported. Using default: ${DEFAULT_LOCALE}`
        );
        languageLocale = DEFAULT_LOCALE;
      }

      await i18n.changeLanguage(languageLocale);
      setDayJSLocale(languageLocale);
      setMomentLocale(languageLocale);
      setI18nLanguageStorage(languageLocale);
    },
    [i18n]
  );

  const getCurrentLanguage = useCallback(() => {
    const currentLang = i18n.language;
    return supportedLanguages.find((lang) => lang.locale === currentLang);
  }, [i18n.language, supportedLanguages]);

  const currentLanguage = useMemo(
    () =>
      getCurrentLanguage() ?? {
        ...SUPPORTED_LANGUAGES[DEFAULT_LOCALE],
        locale: DEFAULT_LOCALE,
      },
    [getCurrentLanguage]
  );

  return {
    currentLanguage,
    getCurrentLanguage,
    handleChangeLanguage,
    supportedLanguages,
    t,
  };
}
